import { createPortal } from 'react-dom';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import DrawerHeader from './components/DrawerHeader';
import SectionBlock from './components/SectionBlock';
import DegradedBanner from './components/DegradedBanner';
import { UnitsProvider } from './UnitsContext';
import { NO_RECONS_IN_SCOPE, RECON_CAVEATS, RECONCILIATIONS_UNAVAILABLE } from './constants/reconciliationsCopy';
import { useReconciliationsLogic } from './hooks/useReconciliationsLogic';
import { selectSectionsForScope, selectReconLabelsById } from '../../../../store/reconciliations/selectors';
import styles from './ReconciliationsDrawer.module.scss';
import type { ReconScope } from '../../../../store/reconciliations/constants';

interface ReconciliationsDrawerProps {
    orgId: string;
    scope: ReconScope;
    onClose: () => void;
}

/** Desktop only — no mobile entry point exists for Reconciliations (PD11). */
function ReconciliationsDrawer({ orgId, scope, onClose }: Readonly<ReconciliationsDrawerProps>) {
    const { entry, availableFys, selectedFys, setSelectedFys } = useReconciliationsLogic(orgId);
    const sections = selectSectionsForScope(entry, scope);
    const degraded = entry?.payload?.degraded;

    // Two different nothings. No audited FY is a fact about the company; an empty scope is a fact
    // about this view — and the only way a full payload can produce one is a recon/section id
    // drift between the two repos (§4.1), which must never read as "this company has no audited
    // financials".
    const emptyState = !entry?.payload?.availableFys.length
        ? RECON_CAVEATS.ZERO_AUDITED_YEARS
        : !sections.length
        ? NO_RECONS_IN_SCOPE
        : null;

    // One region, always mounted, whose TEXT changes — assistive tech announces an update to a
    // live region's content, and says nothing when one element is swapped out for another.
    const stateMessage =
        !entry || entry.status === 'loading' ? 'Loading…' : entry.status === 'error' ? RECONCILIATIONS_UNAVAILABLE : emptyState ?? '';

    // The title sits in this drawer's own header row rather than SideDrawer's `showHeading` +
    // `headerActions` pair, which renders two boxes with the second absolutely positioned over the
    // first — that is what put the title and the controls on different lines. `heading` is still
    // passed: it is the dialog's aria-label. SideDrawer keeps rendering the close button, and the
    // header row reserves the space it is absolutely positioned into.
    //
    // Portalled to document.body: the tab pills mount this drawer inside a sticky, z-indexed
    // header row (Financials/Banking `.Selector`, GST `.GstPage` isolation), and a fixed backdrop
    // cannot escape its ancestor's stacking context — the dashboard header then paints over it.
    return createPortal(
        <SideDrawer allowClose onClose={onClose} width="77.375rem" heading="Reconciliations">
            <UnitsProvider>
                <div className={styles.Drawer}>
                    <DrawerHeader availableFys={availableFys} selectedFys={selectedFys} onFysChange={setSelectedFys} />
                    <div className={styles.Body}>
                        <div className={styles.Content}>
                            <div role="status" aria-live="polite" className={stateMessage ? styles.State : styles.SrOnly}>
                                {stateMessage}
                            </div>
                            {entry?.status === 'success' && degraded?.isDegraded && (
                                <DegradedBanner degraded={degraded} reconLabelById={selectReconLabelsById(entry)} />
                            )}
                            {entry?.status === 'success' &&
                                !emptyState &&
                                sections.map((section) => (
                                    <SectionBlock
                                        key={section.id}
                                        orgId={orgId}
                                        fys={selectedFys.length ? selectedFys : null}
                                        section={section}
                                    />
                                ))}
                        </div>
                    </div>
                </div>
            </UnitsProvider>
        </SideDrawer>,
        document.body,
    );
}

export default ReconciliationsDrawer;
